import React from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contextProviders/AuthProvider';
import { useSiteMode } from '../contextProviders/SiteModeProvider';
import RegistrationClosed from '../pages/Auth/RegistrationClosed';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface SiteModeGateProps {
  /** Shown to a stranger while the pre-launch flag is on. */
  closed: React.ReactElement;
  /** Shown to everybody else -- the real page. */
  open: React.ReactElement;
}

/**
 * Picks which of two pages a visitor gets, based on the pre-launch flag.
 *
 * Two rules, both deliberate:
 *
 * - Signing in beats the flag. Anybody with a session -- the admin who has
 *   to reach the switch that turns this off, a tester, an early host --
 *   always gets the real app. A flag that could shut its own owner out of
 *   the panel that flips it is a flag nobody dares turn on.
 * - It waits. Rendering the app for one frame and then replacing it with a
 *   "coming soon" page shows a stranger exactly the thing the flag exists to
 *   hide, and the flicker is worse than the wait, which is one small GET.
 *
 * None of this is a security boundary and it is not trying to be. What
 * actually must not happen while the platform is closed -- somebody creating
 * an account -- is refused by the API inside AuthController.Register, whether
 * or not this component ever renders.
 */
/** The same full-screen wait PublicRoute shows, while the flags are still in flight. */
const SiteModeLoading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <Spin size="large" />
      <p style={{ color: '#666', fontSize: 14 }}>{translate(t, TRANSLATIONS.LAYOUT_LOADING)}</p>
    </div>
  );
};

const SiteModeGate: React.FC<SiteModeGateProps> = ({ closed, open }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { siteMode, loading: siteModeLoading } = useSiteMode();

  if (authLoading || siteModeLoading) return <SiteModeLoading />;
  if (siteMode.landingModeEnabled && !isAuthenticated) return closed;
  return open;
};

/**
 * The registration form, or the "not open yet" page in its place.
 *
 * Separate from SiteModeGate because it answers a different flag and has no
 * signed-in escape hatch: unlike the landing, which only decides what a
 * stranger looks at, this one shadows a rule the server enforces on its own.
 * Showing the form to anybody while the API would refuse the POST just moves
 * the disappointment to the end of the flow.
 */
export const RegistrationGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { siteMode, loading } = useSiteMode();

  if (loading) return <SiteModeLoading />;
  if (!siteMode.registrationEnabled) return <RegistrationClosed />;
  return children;
};

export default SiteModeGate;
